import { useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logoHP from "@/assets/logo-habitat-prestige.png?w=400&format=webp";

const navLinks = [
  { label: "Accueil", href: "#accueil" },
  { label: "Services", href: "#services" },
  { label: "Pourquoi nous", href: "#pourquoi" },
  { label: "Zone", href: "#zone" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "#contact" },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (!href.startsWith("#")) {
      navigate(href);
      return;
    }
    const anchor = href.replace("#", "");
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-secondary/95 backdrop-blur-md border-b border-secondary">
      <div className="container-custom flex items-center justify-between h-16 md:h-18 px-4 lg:px-8">
        {/* Logo */}
        <a href="/" onClick={handleLogoClick} className="flex items-center">
          <img src={logoHP} alt="Habitat Prestige 89 – Artisan peinture et rénovation à Sens" className="h-10 md:h-12 w-auto" width={201} height={48} />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-secondary-foreground/80 hover:text-primary font-medium text-sm transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Phone CTA - desktop full */}
        <a
          href="tel:+33632864661"
          className="hidden lg:flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-heading font-bold text-sm hover:brightness-110 transition-all duration-200"
          style={{ boxShadow: "var(--shadow-cta)" }}
        >
          <Phone size={18} />
          06 32 86 46 61
        </a>

        {/* Phone + Menu toggle on mobile/tablet */}
        <div className="flex lg:hidden items-center gap-3">
          <a
            href="tel:+33632864661"
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition-all"
            aria-label="Appeler"
          >
            <Phone size={18} />
          </a>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-secondary-foreground"
            aria-label="Menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-secondary border-t border-secondary overflow-hidden"
          >
            <nav className="flex flex-col p-4 gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { handleNavClick(e, link.href); setMenuOpen(false); }}
                  className="text-secondary-foreground/80 hover:text-primary font-medium py-2 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:+33632864661"
                className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-lg font-heading font-bold mt-2"
              >
                <Phone size={18} />
                06 32 86 46 61
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
